import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { StylesConfig } from 'react-select';
import HttpService from 'shared/services/http.service';
import i18n from 'shared/util/localization';
import localizationConstants from 'shared/util/translation/constants';

const firstLevelBreadcrumbs = [
	{ name: localizationConstants.home, link: '/' }
];

const API_CONFIG = {
	baseUrl: `${process.env.REACT_APP_BASE_URL}`,
	path: {
		login: 'auth/login',
		googleLogin: 'auth/login/google',
		magicLogin: 'auth/login/magic/generate',
		magicLoginVerify: 'auth/login/magic/process',
		logout: 'logout',
		forgetPassword: 'auth/forgot-password',
		resetPassword: 'auth/password/reset',
		profile: 'profile',
		changePassword: 'change-password',
		users: 'users',
		emailTemplates: 'email-templates',
		organizations: 'organizations',
		organizationsDetail: 'organizations/detail',
		orgOptions: 'org/option/fields',
		paymentStatus: 'organizations/payment-status/license',
		organizationLabels: 'labels',
		universeCustmerChangePassword: 'customers/change-password',
		permissions: 'dropdown/permissions',
		permissionsByModule: 'permissions/route/by-module',
		incomingLicenses: 'licenses/incoming',
		incomingLicensesList: 'organizations/incoming-license-requests',
		customersToRelate: 'dropdown/customers/search',
		relateCustomer: 'organizations/incoming-license-requests/assign-customer',
		newCustomer: 'organizations/incoming-license-requests/new-customer',
		machineList: 'licenses/machine-list',
		customerList: 'licenses/machine-customers',
		autoUpdateMachineList: 'licenses/auto-update-machine-list',
		autoUpdateMachine: 'licenses/auto-update-machines',
		licenseNumbers: 'licenses/license-numbers',
		requestLicenseDetails: 'licenses/license-number/detail',
		orderCodeExtensionList: 'order-code-extension',
		orderCodeChangeStatus: 'order-code-extension/change-status',
		configurations: 'licenses/configurations',
		configuration: 'licenses/configuration',
		roles: 'roles',
		dropdownRoles: 'dropdown/roles',
		customerLicense: 'customer-license',
		customerLicenseForSupport: 'organizations/support/customer-license',
		getCustomerDetailsForSupport: 'organizations/support/license/customer',
		customerLicenseModules: 'licenses/module-list',
		licenseModules: 'license-modules',
		autoGeneratedLicense: 'graphs/auto-generated-licenses',
		upcomingCreatedLicense: 'graphs/upcoming-created-licenses',
		customerInfo: 'customer-address',
		credentials: 'bird-hub/credentials',
		randomCredential: 'generate-random-credential',
		confirmValidationAction: 'phoenix/company/confirm-validation-action',
		validateCompanyEmail: 'phoenix/company/confirm-validation',
		companyCreation: 'phoenix/company/check-status',
		phoenixCompanyOnboarding: 'phoenix/company/onboarding',
		phoenixForgotPassword: 'phoenix/forgot-password',
		phoenixResetPassword: 'phoenix/reset-password',
		phoenixIncomingLicenseList: 'organizations/phoenix/incoming-license-requests',
		phoenixRelateCustomer: 'organizations/phoenix/incoming-license-requests/assign-customer',
		phoenixNewCustomer: 'organizations/phoenix/incoming-license-requests',
		phoenixCompanies: 'phoenix-companies',
		phoenixBraches: 'phoenix-branches',
		birdHubClients: 'birdhub/clients',
		birdHubOrders: 'birdhub/orders',
		birdHubOrderDetail: 'birdhub/orders/detail',

		resigoLicenseCustomer: 'customer-resigo-license',
		resigoAdminMachines: 'resigo/machines',
		resigoAdminLicenses: 'resigo/licenses',
		resigoLicenseDecoder: 'resigo/licenses/detail',

		phoenixModules: 'phoenix-modules',
		phoenixLicense: 'customer-phoenix-license',
		jumpbirdCompanies: 'jumpbird-companies',
		jumpbirdIncomingLicenseList: 'organizations/jumpbird/incoming-license-requests',
		jumpbirdRelateCustomer: 'organizations/jumpbird/incoming-license-requests/assign-customer',
		jumpbirdNewCustomer: 'organizations/jumpbird/incoming-license-requests',
		jumpbirdLicense: 'customer-jumpbird-license',
		orgIncomingPhoenixLicense: 'organizations/phoenix/incoming-license-requests',
		dropdownPackages: 'dropdown/packages',
		dropdownPhoenixModules: 'dropdown/phoenix-modules',
		phoenixPackages: 'phoenix-packages',
		jumpbirdSubscriptions: 'jumpbird-subscriptions',
		jumpbirdSubscriptionBillingCycles: 'jumpbird-subscriptions/package-billing-detail',
		phoenixApiKeys: 'phoenix-apikeys',
		partnerSearch: 'dropdown/partner/search',
		customersMachineActiveLicense: 'customers/machine-active-license',
		updateCompany: 'phoenix-companies',
		updateJumpbirdCompany: 'jumpbird-companies',
		updateMachineData: 'organizations/incoming-license-requests',
		tseDevice: 'tse/devices',
		tseDeviceActivity: 'activity-log/TseDevice',
		tseDeviceInfo: 'tse/devices',
		tseCustomers: 'customers',
		tseCustomersV2: 'tse-admin/customers',
		tseAdminCustomerDetail: 'tse-admin/customer-detail',
		tseCustomerDetail: 'customers/detail',
		tseCustomerBackupStatistics: 'tse-admin/backup/statistics',
		tseCustomerBackupList: 'tse-admin/backup/history',
		tseCustomerBackupDownloadRequest: 'tse-admin/backup/download-request',
		tseCustomerBackupDownloadLink: 'tse-admin/backup-download-link',
		tseBackupUploadStatistics: 'tse-admin/upload-statistics',
		tseCustomerStatistics: 'tse-admin/customer/statistics',
		tseCustomerRegister: 'customers/register',
		s3Cred: 'zm-list-tool/upload-file/temp-credentials',
		invoicesS3Cred: 'invoice-groups/upload-file/temp-credentials',
		invoiceGroupCSVUploadRequest: 'invoice-groups/request',
		csvUploadRequest: 'zm-list-tool/request',
		zmListTool: 'zm-list-tool',
		zmListToolRequest: 'zm-list-tool/request',
		zmListEntryStatus: 'status',
		downloadFileSignedUrl: 'download-file/signed-url',
		pipedriveCustomer: 'pipe-drive/org/new-customer',
		partner: 'partner/search',
		queueStatus: 'queue-status',
		turnover: 'turnover',
		allReaList: 'rea/list',
		allProvisionReport: 'partner-provision/reports',
		activeProvisionReport: 'partner-provision/reports/active',
		archivedProvisionReport: 'partner-provision/reports/archived',
		reportArchive: 'partner-provision/reports/archive',
		reportRestore: 'partner-provision/reports/restore',
		invoiceGroupList: 'invoice-groups',
		activeInvoiceGroupList: 'invoice-groups/active',
		archivedInvoiceGroupList: 'invoice-groups/archived',
		archiveInvoiceGrouup: 'invoice-groups/archive',
		restoreInvoiceGrouup: 'invoice-groups/restore',
		invoices: 'invoices',
		createInvoice: 'invoices/create',
		createInvoiceWithEmail: 'invoices/create-with-email',
		createInvoiceSendEmail: 'invoices/create-invoice-send-email',
		removeInvoice: 'invoices/remove',
		markInvoiceAsComplete: 'invoices/update-status',
		generateInvoicePDF: 'invoices/generate-pdf',
		statusChangeOfInvoice: 'invoices/change-status',
		sevDeskInventories: 'sev-desk/inventories',
		listInventories: 'list-inventories',
		inventoryGrouup: 'inventory-groups',
		getInventorySuggestion: 'inventory-groups/inventory/suggestion',
		createInventoryGroup: 'inventory-groups/create-group',
		changeInventoriesStatus: 'inventories/change-status',
		changeInventoriesGroupStatus: 'inventory-groups',
		taxRules: 'tax-rules',
		documentsInvoicesCustomers: 'documents/invoices/customers',
		documentsInvoicesDownload: 'documents/invoices/download',
		taxRulesCountries: 'tax-rules/countries',
		sevDeskTaxRules: 'tax-rules/sev-desk',
		provisionCreateNote: 'partner-provision/credit-note',
		changePartnerCommissionStatus: 'partner-provision/partner-commission/status',
		changeCustomerCommissionStatus: 'partner-provision/customer-commission/status',
		reaListFilterConfig: 'partner-provision/report-generate-config', // TODO: separate path
		reportGenerateConfig: 'partner-provision/report-generate-config',
		partnerCommission: 'partner-provision/partner-commission',
		customerCommission: 'partner-provision/customer-commission',
		updatePartnerPercentage: 'partner-provision/partner/update-provision-percentage',
		customerPercentageUpdate: 'partner-provision/customer/update-percentage',
		revertCustomerCommission: 'partner-provision/customer-commission/reset',
		revertCustomerCommissionStatus: 'partner-provision/customer-commission/status/reset',
		revertCustomerPercentage: 'partner-provision/customer/reset-provision-percentage',
		invoicePositions: 'invoice-positions',
		history: 'history',
		getHourPbpw: 'pbpw',
		getHourPbpwCustom: 'pbpw/custom-date',
		pbpwList: 'pbpw/list',
		uploadPasswordFile: 'pbpw/upload-file/temp-credentials',
		uploadPasswordRequest: 'pbpw/store-password',

		bundleGroups: 'offer-tool-management/bundle-groups',
		singleBundleGroup: 'offer-tool-management/bundle-group',
		removeBundleFromTheBundleGroup: 'offer-tool-management/bundle-group/remove-bundle',
		addBundlesToTheBundleGroup: 'offer-tool-management/bundle-group/add-bundle',

		bundles: 'offer-tool-management/bundles',
		singleBundle: 'offer-tool-management/bundle',
		bundleProducts: 'offer-tool-management/products-bundle',
		removeProductFromTheBundle: 'offer-tool-management/bundle/remove-product',
		addProductsToTheBundle: 'offer-tool-management/bundle/add-product',
		bundleS3Cred: 'offer-tool-management/bundle/upload-file/temp-credentials',

		products: 'offer-tool-management/products',
		addProduct: 'offer-tool-management/add-product',
		singleProduct: 'offer-tool-management/product',
		productS3Cred: 'offer-tool-management/product/upload-file/temp-credentials',

		taxPayer: 'tse-register/taxpayer',
		establishmentList: 'tse-register/establishments',
		establishment: 'tse-register/establishment',
		tss: 'tse-register/tss',
		customer: 'tse-register/customer'
	},
	sentry: 'https://c1308bc472d740f78185cffc17485c87@sentry.io/1483874'
};

const getUrl = (url: string, params: { [key: string]: any } = {}): string => {
	let urlString = `${API_CONFIG.baseUrl}/${url}`;
	if (params && !isEmpty(params)) {
		urlString += `?${queryString.stringify(params)}`;
	}
	return urlString;
};

const allPermissionMapping: { [key: string]: string; } = {
	emailTemplateVariableDelete: 'emailTemplateVariable.destroy',
	emailTemplateVariableCreate: 'emailTemplateVariable.store',
	emailTemplateDelete: 'emailTemplate.destroy',
	emailTemplateEdit: 'emailTemplate.update',
	emailTemplateView: 'emailTemplate.show',
	emailTemplateCreate: 'emailTemplate.store',
	emailTemplateModule: 'emailTemplate.index',

	usersCreate: 'users.store',
	usersRead: 'users.show',
	usersUpdate: 'users.update',
	usersDelete: 'users.destroy',
	usersModule: 'users.index',

	archivedUsersModule: 'users.archive',
	archivedUserRestore: 'users.restore',

	rolesCreate: 'roles.store',
	rolesRead: 'roles.show',
	rolesUpdate: 'roles.update',
	rolesDelete: 'roles.destroy',
	rolesModule: 'roles.index',

	permissionsModule: 'permissions.index',
	permissionsRead: 'permissions.show',
	permissionsCreate: 'permissions.store',
	permissionsUpdate: 'permissions.update',
	permissionsDelete: 'permissions.destroy',

	widgetPserStatistics: 'widget.userStatistics',

	turnoverStatistics: 'invoice.turnover-statistics',

	reaList: 'toolREA.reaList',

	organizationsModule: 'organizations.index',
	organizationsCustomerModule: 'organizations.customerIndex',
	organizationsSuperAdminModule: 'organizations.superAdminIndex',
	organizationsLicenseModule: 'organizations.licenseIndex',
	organizationsSupportModule: 'organizations.supportIndex',
	organizationsTimeline: 'organizations.timeline',
	organizationsSuperAdminRead: 'organizations.superAdminShow',
	organizationsSupportRead: 'organizations.supportShow',
	organizationsSupportLicenseOrganizationDetail: 'organizations.supportLicenseOrganizationDetail',
	supportCustomerLicenseMachineList: 'support.customerLicense.machineList',
	supportCustomerLicenseMachineLicenses: 'support.customerLicense.machineLicenses',
	organizationsLicenseRead: 'organizations.licenseShow',
	organizationsMetaDataGet: 'organizations.metaData.get',
	organizationsMetaData: 'organizations.metaData',

	organizationsIncomingLicenseModule: 'incomingLicense.incomingRequestList',
	IncomingLicenseRelateExistingCustomer: 'incomingLicense.incomingAssignCustomer',
	IncomingLicenseRelateNewCustomer: 'incomingLicense.createNewCustomer',
	organizationsIncomingLicenseRead: 'incomingLicense.incomingRequest.show',
	phoenixIncomingLicenseRead: 'phoenix.incomingLicenseRequest.show',

	autoGeneratedLicenseGraphView: 'graphs.autoGeneratedLicenseGraph',
	upcomingCreatedLicenseGraphView: 'graphs.upcomingCreatedLicenses',

	licensesIncoming: 'licenses.incoming',
	licensesLicenseNumbers: 'licenses.license_numbers',
	licensesMachineList: 'licenses.machine_list',
	autoUpdateMachineList: 'licenses.autoUpdateMachineList',
	autoUpdateMachine: 'licenses.autoUpdateMachine',
	licensesMachineCustomers: 'licenses.machine_customers',

	licensesConfigurationList: 'licenses.configuration.list',
	licensesConfigurationDetail: 'licenses.configuration.detail',
	licensesConfigurationUpdate: 'licenses.configuration.update',

	orderCodeExtensionsList: 'order-code-extensions.list',
	orderCodeExtensionsChangeStatus: 'order-code-extensions.change-status',

	customerLicenseMachineModule: 'customerLicense.machineIndex',
	customerLicenseMachineRead: 'customerLicense.machineShow',
	customerLicenseMachineCreate: 'customerLicense.machineStore',
	customerLicenseMachineUpdate: 'customerLicense.machineUpdate',
	customerLicenseMachineLicenses: 'customerLicense.machineLicenses',
	customersMachineActiveLicense: 'customerLicenses.machineActiveLicense',
	customerLicenseFutureLicenseActivate: 'customerLicense.futureLicenseActivate',
	incomingLicenseMachineDataUpdate: 'incomingLicense.updateMachineData',

	customerLicenseModuleList: 'customerLicense.moduleList',
	customerLicenseModuleCreate: 'customerLicense.moduleStore',
	customerLicenseModuleUpdate: 'customerLicense.moduleUpdate',
	customerLicenseDelete: 'customerLicense.deleteLicense',

	licenseModuleCreate: 'licenseModule.store',
	licenseModuleUpdate: 'licenseModule.update',
	licenseModuleStatusChange: 'licenseModule.changeStatus',
	licenseModuleGetLicenseNumberDetail: 'licenseModule.getLicenseNumberDetail',

	customerLicenseLicenseCreate: 'customerLicense.licenseStore',
	customerLicenseLicenseUpdate: 'customerLicense.licenseUpdate',
	customerLicenseSendEmail: 'customerLicense.sendEmail',

	birdhubCredentialList: 'bird_hub.credentials.index',
	birdhubCredentialRead: 'bird_hub.credentials.show',
	birdhubCredentialCreate: 'bird_hub.credentials.store',
	birdhubCredentialUpdate: 'bird_hub.credentials.update',
	birdhubOrdersList: 'birdhub.orders.list',
	birdhubOrdersDetail: 'birdhub.orders.detail',
	birdhubClientsList: 'birdhub.clients.list',

	phoenixIncomingLicenseModule: 'phoenix.incomingLicenseRequestList',
	phoenixIncomingLicenseRelateExistingCustomer: 'phoenix.incomingAssignCustomer',
	phoenixIncomingLicenseRelateNewCustomer: 'phoenix.createNewCustomer',
	phoenixIncomingLicenseSetUnlimitedExpiryDate: 'phoenix.incomingLicenseRequest.setUnlimitedExpiryDate',
	phoenixModuleList: 'phoenix.modules.index',
	phoenixModuleCreate: 'phoenix.modules.store',
	phoenixModuleUpdate: 'phoenix.modules.update',
	phoenixModuleDelete: 'phoenix.modules.destroy',
	phoenixModulesCreateNewModule: 'phoenixModules.create-new-module',
	phoenixModulesUpdateModule: 'phoenixModules.update-module',
	phoenixModulesChangeModuleStatus: 'phoenixModules.change-module-status',
	phoenixCompanyList: 'phoenix.companies.index',
	phoenixCompanyRead: 'phoenix.companies.show',
	phoenixCompanyUpdate: 'phoenix.companies.update',
	phoenixCompaniesRequestPassword: 'phoenix.companies.requestPassword',
	phoenixCompanySubscriptionList: 'phoenix.subscriptions.index',
	phoenixCompanySubscriptionRead: 'phoenix.subscriptions.show',
	phoenixCompaniesRequestResetPassword: 'phoenix.companies.requestResetPassword',
	phoenixBranchesIndex: 'phoenix.branches.index',

	jumpbirdIncomingLicenseModule: 'jumpbird.incomingCompanyRequestList',
	jumpbirdIncomingCompanyRequestShow: 'jumpbird.incomingCompanyRequest.show',
	jumpbirdIncomingCompanyRequestAssignCustomer: 'jumpbird.incomingCompanyRequest.assignCustomer',
	jumpbirdIncomingCompanyRequestCreateNewCustomer: 'jumpbird.incomingCompanyRequest.createNewCustomer',
	jumpbirdCustomerLicenseListCompanies: 'jumpbirdCustomerLicense.listCompanies',
	jumpbirdCustomerLicenseUpdateSubscription: 'jumpbirdCustomerLicense.updateSubscription',
	jumpbirdCompaniesList: 'jumpbird.companies.list',
	jumpbirdCompaniesViewDetail: 'jumpbird.companies.viewDetail',
	jumpbirdCompaniesUpdateCompanyData: 'jumpbird.companies.updateCompanyData',
	jumpbirdSubscriptionsListPackages: 'jumpbird.subscriptions.listPackages',
	jumpbirdCustomerLicenseManageSepa: 'jumpbirdCustomerLicense.manage-sepa',
	jumpbirdCustomerLicenseChangeBlockStatus: 'jumpbirdCustomerLicense.changeBlockStatus',
	jumpbirdCustomerLicenseAssignPackage: 'jumpbirdCustomerLicense.assign-package',
	jumpbirdCustomerLicenseCancelSubscription: 'jumpbirdCustomerLicense.cancel-subscription',
	jumpbirdCustomerLicenseSetSubscriptionPackageEndDate: 'jumpbirdCustomerLicense.setSubscriptionPackageEndDate',

	phoenixPackageList: 'phoenix.packages.index',
	phoenixPackageUpdate: 'phoenix.packages.update',
	phoenixPackagesUpdateStatus: 'phoenix.packages.update-status',

	phoenixApiKeyList: 'phoenix.api-key.list',

	// -----------------------------------------Resigo--------------------------------------------

	// Resigo Machine
	resigoCustomerLicenseListMachines: 'resigoCustomerLicense.listMachines',
	resigoCustomerLicenseAddMachine: 'resigoCustomerLicense.addMachine',
	resigoCustomerLicenseUpdateMachine: 'resigoCustomerLicense.updateMachine',

	// Resigo License
	resigoCustomerLicenseAddMachineLicense: 'resigoCustomerLicense.addMachineLicense',
	resigoCustomerLicenseListMachineLicenses: 'resigoCustomerLicense.listMachineLicenses',
	resigoCustomerLicenseUpdateLicense: 'resigoCustomerLicense.updateLicense',
	resigoCustomerLicenseDeleteLicense: 'resigoCustomerLicense.deleteLicense',
	resigoCustomerLicenseSendLicenseEmail: 'resigoCustomerLicense.sendLicenseEmail',

	// Resigo Admin Machine
	resigoAdminListMachines: 'resigoAdmin.listMachines',
	resigoAdminUpdateMachineData: 'resigoAdmin.updateMachineData',
	resigoAdminViewMachineDetail: 'resigoAdmin.viewMachineDetail',
	resigoAdminDecodeLicenseDetail: 'resigoAdmin.decodeLicenseDetail',

	// Resigo Admin License
	resigoAdminListLicenses: 'resigoAdmin.listLicenses',

	tseDeviceList: 'tse.deviceList',
	tseCreateDevice: 'tse.createDevice',
	tseViewDeviceDetail: 'tse.viewDeviceDetail',
	tseActivityLog: 'tse.activityLog',
	tseImportDevice: 'tse.importDevice',
	activateDevice: 'tse.activateDevice',
	tseBackupUploadStatisticsGraphView: 'tseAdmin.uploadStatistics',
	tseCustomerStatistics: 'tseAdmin.customerStatistics',
	tseCustomerList: 'universeCustomer.customers',
	tseCustomerRead: 'universeCustomer.customerDetail',
	tseCustomerListV2: 'tseAdmin.customers',
	tseCustomerBackupStatisticsRead: 'tseAdmin.backupStatistics',
	tseBackupHistoryList: 'tseAdmin.backupHistory',
	tseDownloadRequestCreate: 'tseAdmin.downloadRequest',
	tseLatestBackupDataRead: 'tseAdmin.latestDownloadRequest',
	tseDownloadBackup: 'tseAdmin.downloadBackup',
	getTseCustomerDetail: 'tseAdmin.get-tse-customer-detail',
	tseCustomerRegistration: 'universeCustomer.customerRegistration',
	tseUniverseCustomerRegistration: 'universeCustomer.customerRegistration',
	universeCustomerChangePassword: 'universeCustomer.changeCustomerPassword',

	zmListGetRequests: 'zmList.getRequests',
	zmListAddRequest: 'zmList.addRequest',
	zmListGetRequestStatus: 'zmList.getRequestStatus',
	zmListRequestDownloadOutputFile: 'zmList.requestDownloadOutputFile',
	zmListGetUploadFileTempCredential: 'zmList.getUploadFileTempCredential',

	pbpwGetCurrentAndNextHourPasswords: 'pbpw.getCurrentAndNextHourPasswords',
	pbpwListPosbillPasswords: 'pbpw.listPosbillPasswords',
	pbpwStore: 'pbpw.storePasswords',
	pbpwGetUploadFileTempCredential: 'pbpw.getUploadFileTempCredential',
	pbpwGetCurrentAndNextHourPasswordsOfCustomDate: 'pbpw.getCurrentAndNextHourPasswordsOfCustomDate',

	organizationPaymentStatusDetail: 'organizations.payment_status.detail',
	organizationPaymentStatusDynamicFormFieldGenerate: 'organizations.payment_status.edit',
	organizationPaymentStatusFormFieldUpdate: 'organizations.payment_status.update',
	organizationPaymentStatusSendEmail: 'organizations.payment_status.sendEmail',
	organizationsGetOrganizationDetail: 'organizations.getOrganizationDetail',

	allPartnerReportList: 'partnerProvision.list-all-provision-reports',
	activePartnerReportList: 'partnerProvision.list-active-provision-reports',
	archivedPartnerReportList: 'partnerProvision.list-archived-provision-reports',
	archiveProvisionReport: 'partnerProvision.archive-provision-report',
	restoreProvisionReport: 'partnerProvision.restore-provision-report',
	partnerReportDetail: 'partnerProvision.reportDetail',
	partnerProvisionCreateCreditNote: 'partnerProvision.createCreditNote',
	partnerProvisionRecreateCreditNote: 'partnerProvision.recreateCreditNote',
	partnerProvisionRemoveCreditNote: 'partnerProvision.removeCreditNote',
	partnerProvisionGenerateBulkCreditNote: 'partnerProvision.generateBulkCreditNote',
	updatePartnerCommissionStatus: 'partnerProvision.updatePartnerCommissionStatus',
	partnerReportExport: 'partnerProvision.reportExport',
	reportPartnerList: 'partnerProvision.reportPartners',
	partnerReportGenerate: 'partnerProvision.generateReport',
	partnerCommissionDelete: 'partnerProvision.deletePartnerCommission',
	customerCommissionCreate: 'partnerProvision.reportAddCustomer',
	customerCommissionDelete: 'partnerProvision.deleteCustomerCommission',
	customerCommissionUpdate: 'partnerProvision.updateCustomerCommission',
	customerCommissionStatusUpdate: 'partnerProvision.customerCommissionStatusUpdate',
	customerCommissionPercentageUpdate: 'partnerProvision.updateCustomerCommissionPercentage',
	customerCommissionRevert: 'partnerProvision.resetCustomerCommission',
	customerCommissionStatusRevert: 'partnerProvision.resetCustomerCommissionStatus',
	partnerCommissionPercentageUpdate: 'partnerProvision.updatePartnerProvisionPercentage',
	partnerProvisionResetCustomerCommissionPercentage: 'partnerProvision.resetCustomerCommissionPercentage',
	partnerProvisionExportPartnerReport: 'partnerProvision.exportPartnerReport',

	customerPhoenixLicenseSetUnlimitedExpiryDate: 'customerPhoenixLicense.canSetUnlimitedExpiryDate',
	customerPhoenixLicenseChangeMasterPassword: 'customerPhoenixLicense.changeMasterPassword',
	customerPhoenixLicenseCompanyList: 'customerPhoenixLicense.companyIndex',
	customerPhoenixLicenseCompanyUpdate: 'customerPhoenixLicense.companyUpdate',
	customerPhoenixLicenseModuleList: 'customerPhoenixLicense.modulesIndex',
	customerPhoenixLicenseModuleUpdate: 'customerPhoenixLicense.modulesUpdate',
	customerPhoenixLicenseSubscriptionList: 'customerPhoenixLicense.subscriptionsIndex',
	customerPhoenixLicenseSubscriptionRead: 'customerPhoenixLicense.subscriptionsShow',
	customerPhoenixLicenseSubscriptionCreate: 'customerPhoenixLicense.subscriptionsStore',

	invoicesList: 'invoices.list',
	invoicesGetUploadFileTempCredential: 'invoices.getUploadFileTempCredential',
	invoicesGroupsList: 'invoices.groups.list',
	activeInvoicesGroupsList: 'invoices.groups.list-active-invoice-groups',
	archivedInvoicesGroupsList: 'invoices.groups.list-archived-invoice-groups',
	invoicesCreate: 'invoices.create',
	invoicesRemoveInvoice: 'invoices.removeInvoice',
	invoiceGroupsAddRequest: 'invoice-groups.addRequest',
	listInventoryGroup: 'inventory-group.listInventoryGroup',
	inventories: 'list.inventories',
	inventoriesStatus: 'inventories.changeDisplayInInvoiceStatus',
	createInventoryGroup: 'inventory-group.createInventoryGroup',
	getInventoryGroup: 'inventory-group.getInventoryGroup',
	updateInventoryGroup: 'inventory-group.updateInventoryGroup',
	deleteInventoryGroup: 'inventory-group.deleteInventoryGroup',
	getSuggestion: 'inventory-group.getSuggestion',
	invoicesCreateAndSendEmail: 'invoices.create-and-send-email',
	invoicesCreateInvoiceAndSendEmailMarkComplete: 'invoices.create-invoice-and-send-email-mark-complete',
	invoicesGenerateShopify: 'invoices.generate-shopify',
	invoicesGroupsArchive: 'invoices.groups.archive',
	invoicesGroupsRestore: 'invoices.groups.restore',
	invoicesUpdateStatus: 'invoices.update-status',
	invoicesChangeStatus: 'invoices.change-status',
	invoicesGeneratePDF: 'invoices.generatePDF',
	invoicesCustomersList: 'invoices.customers.list',
	invoicesCustomersDocumentsList: 'invoices.customers.documents.list',
	invoicesCustomersDocumentsDownload: 'invoices.customers.documents.download',

	sevDeskInventoriesSync: 'sev-desk.inventories.sync',
	sevDeskInventoriesList: 'sev-desk.inventories.list',

	taxRulesListSevDeskTaxRules: 'tax-rules.list-sev-desk-tax-rules',

	historyLogMachine: 'history-log.list-posbill-machine-history',
	historyLogLicense: 'history-log.list-posbill-license-history',
	historyLogPaymentStatus: 'history-log.list-organization-change-history',
	historyLogAutoUpdateStatus: 'history-log.list-posbill-machine-auto-update-history',
	historyLogUserActivities: 'history-log.user-activities',
	historyLogListResigoMachineHistory: 'history-log.list-resigo-machine-history',
	historyLogListResigoLicenseHistory: 'history-log.list-resigo-history-log.list-resigo-license-history-history',

	taxRulesListTaxRules: 'tax-rules.list-tax-rules',
	taxRulesCountries: 'tax-rules.countries',
	taxRulesStore: 'tax-rules.store',
	taxRulesDelete: 'tax-rules.delete',

	sevDeskTaxRulesList: 'sev-desk.tax-rules.list',

	offerToolCreateBundleGroup: 'OfferTool.createBundleGroup',
	offerToolUpdateBundleGroup: 'OfferTool.updateBundleGroup',
	offerToolListBundleGroups: 'OfferTool.listBundleGroups',

	offerToolCreateBundle: 'OfferTool.createBundle',
	offerToolUpdateBundle: 'OfferTool.updateBundle',
	offerToolListBundles: 'OfferTool.listBundles',
	offerToolAddBundleToBundleGroup: 'OfferTool.addBundleToBundleGroup',
	offerToolRemoveBundleFromBundleGroup: 'OfferTool.removeBundleFromBundleGroup',
	offerToolGetBundlesOfBundleGroup: 'OfferTool.getBundlesOfBundleGroup',

	offerToolAddProduct: 'OfferTool.addProduct',
	offerToolUpdateProduct: 'OfferTool.updateProduct',
	offerToolGetProducts: 'OfferTool.getProducts',
	offerToolAddProductToBundle: 'OfferTool.addProductToBundle',
	offerToolRemoveProductFromBundle: 'OfferTool.removeProductFromBundle',
	offerToolGetProductDetail: 'OfferTool.getProductDetail',
	offerToolGetBundleProducts: 'OfferTool.getBundleProducts',

	tseRegisterGetTaxpayer: 'tseRegister.getTaxpayer',
	tseRegisterCreateOrUpdateTaxpayer: 'tseRegister.createOrUpdateTaxpayer',

	tseRegisterListEstablishments: 'tseRegister.listEstablishments',
	tseRegisterGetEstablishment: 'tseRegister.getEstablishment',
	tseRegisterCreateEstablishment	: 'tseRegister.createEstablishment',
	tseRegisterUpdateEstablishment	: 'tseRegister.updateEstablishment',
	tseRegisterDeleteEstablishment	: 'tseRegister.deleteEstablishment',

	tseRegisterListTSS: 'tseRegister.listTSS',
	tseRegisterGetTSS: 'tseRegister.getTSS',
	tseRegisterCreateTSS: 'tseRegister.createTSS',
	tseRegisterUpdateTSS: 'tseRegister.updateTSS',
	tseRegisterDeleteTSS: 'tseRegister.deleteTSS',

	tseRegisterListClients: 'tseRegister.listClients',
	tseRegisterCreateClient: 'tseRegister.createClient',
	tseRegisterUpdateClient: 'tseRegister.updateClient',
	tseRegisterDeleteClient: 'tseRegister.deleteClient',

	tseRegisterListCustomers: 'tseRegister.listCustomers',
	tseRegisterCreateCustomer: 'tseRegister.createCustomer',
	tseRegisterUpdateCustomer: 'tseRegister.updateCustomer',
	tseRegisterDeleteCustomer: 'tseRegister.deleteCustomer',

};

const userStatus: { [key: string]: string } = {
	0: localizationConstants.inActive,
	1: localizationConstants.Active
};

const credentialStatus: { [key: string]: string } = {
	0: localizationConstants.inActive,
	1: localizationConstants.active
};

const credentialType: string[] = [localizationConstants.internal, localizationConstants.global, localizationConstants.local];

const dateFields: string[] = ['last_update', 'last_sync_at', 'created_at', 'updated_at'];

export type branchIdKeyType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const branchIds = ['1', '2', '3', '4', '5', '6', '7', '8'];

const passwordTime = [
	{ label: '00', value: '0' },
	{ label: '01', value: '1' },
	{ label: '02', value: '2' },
	{ label: '03', value: '3' },
	{ label: '04', value: '4' },
	{ label: '05', value: '5' },
	{ label: '06', value: '6' },
	{ label: '07', value: '7' },
	{ label: '08', value: '8' },
	{ label: '09', value: '9' },
	{ label: '10', value: '10' },
	{ label: '11', value: '11' },
	{ label: '12', value: '12' },
	{ label: '13', value: '13' },
	{ label: '14', value: '14' },
	{ label: '15', value: '15' },
	{ label: '16', value: '16' },
	{ label: '17', value: '17' },
	{ label: '18', value: '18' },
	{ label: '19', value: '19' },
	{ label: '20', value: '20' },
	{ label: '21', value: '21' },
	{ label: '22', value: '22' },
	{ label: '23', value: '23' },

];

const branchMapper = {
	1: localizationConstants.beauty,
	2: localizationConstants.boutique,
	3: localizationConstants.friseur,
	4: localizationConstants.direktverkauf,
	5: localizationConstants.baeckerei,
	6: localizationConstants.kiosk,
	7: localizationConstants.hospitality,
	8: localizationConstants.retail,
};

const searchDropdownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.yes, value: '1' },
	{ name: localizationConstants.no, value: '0' }
];

const tseDeviceTypeDropDownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: 'Cryptovision', value: '0' },
	{ name: 'CryptovisionV2', value: '3' },
	{ name: 'EPSON', value: '1' },
	{ name: 'Swissbit', value: '2' },
];

const autoUpdateOptions = [
	{ name: localizationConstants.select, value: '' },
	{ name: localizationConstants.on, value: '1' },
	{ name: localizationConstants.off, value: '0' }
];

const statusDropdownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.verified, value: '1' },
	{ name: localizationConstants.pending, value: '0' }
];

const inventoryGroupFilterOptions = () => [
	{ id: 0, label: i18n.t(localizationConstants.all), name: localizationConstants.all, value: '' },
	{ id: 1, label: i18n.t(localizationConstants.priceInInventoryGroup), name: localizationConstants.priceInInventoryGroup, value: '1' },
	{ id: 2, label: i18n.t(localizationConstants.priceNotInInventoryGroup), name: localizationConstants.priceNotInInventoryGroup, value: '0' }
];

const invoiceStatusActionOptions = () => [
	{ id: 0, label: i18n.t(localizationConstants.manual), name: localizationConstants.manual, value: 'manual' },
	{ id: 1, label: i18n.t(localizationConstants.invalid), name: localizationConstants.invalid, value: 'invalid' },
	{ id: 2, label: i18n.t(localizationConstants.reset), name: localizationConstants.reset, value: null }
];

const productSelectionDropDownOption = () => [
	{ label: i18n.t(localizationConstants.posbill), value: 'posbill' },
	{ label: i18n.t(localizationConstants.phoenix), value: 'phoenix' }
];

const orderStatusFilterDropDownOptions = () => [
	{ name: i18n.t(localizationConstants.all), value: '' },
	{ name: i18n.t(localizationConstants.new), value: '1' },
	{ name: i18n.t(localizationConstants.accepted), value: '2' },
	{ name: i18n.t(localizationConstants.processing), value: '3' },
	{ name: i18n.t(localizationConstants.dispatched), value: '4' },
	{ name: i18n.t(localizationConstants.delivered), value: '5' },
	{ name: i18n.t(localizationConstants.rejected), value: '6' },
	{ name: i18n.t(localizationConstants.refused), value: '7' },
	{ name: i18n.t(localizationConstants.cancelled), value: '8' }
];

const birdHubOrderStatusMapper: { [key: string]: string } = {
	1: 'new',
	2: 'accepted',
	3: 'processing',
	4: 'dispatched',
	5: 'delivered',
	6: 'rejected',
	7: 'refused',
	8: 'cancelled',
};

// const sepaStatusOptions = [
// 	{ name: localizationConstants.accept, value: '1' },
// 	{ name: localizationConstants.reject, value: '0' }
// ];

const sepaStatusOptionsForPending = [
	{ name: localizationConstants.accept, value: '1' },
	{ name: localizationConstants.reject, value: '0' }
];

const sepaStatusOptionsForActive = [
	{ name: localizationConstants.reject, value: '0' }
];

const sepaStatusOptionsForBlock = [
	{ name: localizationConstants.accept, value: '1' }
];

const userStatusDropdownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.Active, value: '1' },
	{ name: localizationConstants.inActive, value: '0' }
];

const branchIdDropdownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.beauty, value: '1' },
	{ name: localizationConstants.boutique, value: '2' },
	{ name: localizationConstants.friseur, value: '3' },
	{ name: localizationConstants.direktverkauf, value: '4' },
	{ name: localizationConstants.baeckerei, value: '5' },
	{ name: localizationConstants.kiosk, value: '6' },
	{ name: localizationConstants.hospitality, value: '7' },
	{ name: localizationConstants.retail, value: '8' }
];

const provisionPercentageDropdownOptions = [
	{ name: '0', value: '0' },
	{ name: '5', value: '5' },
	{ name: '10', value: '10' },
	{ name: '15', value: '15' },
	{ name: '20', value: '20' },
	{ name: '25', value: '25' },
	{ name: '30', value: '30' },
	{ name: '35', value: '35' },
	{ name: '40', value: '40' },
	{ name: '45', value: '45' },
	{ name: '50', value: '50' },
];

const isLockedDropdownOptions = [
	{ name: localizationConstants.yes, value: '1' },
	{ name: localizationConstants.no, value: '0' }
];

const isLockedDropdownOptionsForResigo = [
	{ name: localizationConstants.yes, value: true },
	{ name: localizationConstants.no, value: false }
];

const dateStatusDropdownOptions = [
	{ name: localizationConstants.enable, value: 'true' },
	{ name: localizationConstants.disable, value: 'false' }
];

const bundleVisibilityStatusDropdownOptions = [
	{ name: localizationConstants.enable, value: true },
	{ name: localizationConstants.disable, value: false }
];

const mposDropdownOptions = [
	{ name: localizationConstants.enable, value: '1' },
	{ name: localizationConstants.disable, value: '0' }
];

const languageDropdownOptions = [
	{ name: localizationConstants.english, value: 'en' },
	{ name: localizationConstants.german, value: 'de' },
];

const modeTypeDropdownOptions = [
	{ name: localizationConstants.regular, value: '01' },
	{ name: localizationConstants.countable, value: '02' },
];

const modeTypeLabelMapper: { [key: string]: string } = {
	'01': i18n.t(localizationConstants.regular),
	'02': i18n.t(localizationConstants.countable)
};

const colorDropdownOptions = [
	{ name: localizationConstants.red, value: 0 },
	{ name: localizationConstants.blue, value: 1 },
	{ name: localizationConstants.green, value: 2 },
];

const branchOptions = [
	{ name: localizationConstants.beauty, value: '1' },
	{ name: localizationConstants.boutique, value: '2' },
	{ name: localizationConstants.friseur, value: '3' },
	{ name: localizationConstants.direktverkauf, value: '4' },
	{ name: localizationConstants.baeckerei, value: '5' },
	{ name: localizationConstants.kiosk, value: '6' },
	{ name: localizationConstants.hospitality, value: '7' },
	{ name: localizationConstants.retail, value: '8' },
];

const reportStatusDropdownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.pending, value: '0' },
	{ name: localizationConstants.completed, value: '1' },
	{ name: localizationConstants.failed, value: '2' }
];

const enabledDisabledStatusOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.disabled, value: '0' },
	{ name: localizationConstants.enabled, value: '1' },
];

const resigoVersionOptions = [
	{ name: localizationConstants.basic, value: 'basic' },
	{ name: localizationConstants.profi, value: 'profi' },
	{ name: localizationConstants.profiPlus, value: 'profi plus' }
];

const invoiceGroupStatusDropdownOptions = reportStatusDropdownOptions;

const inventoryGroupStatusDropdownOptions = enabledDisabledStatusOptions;

const commissionStatusDropdownOptions = [
	{ name: localizationConstants.all, value: '' },
	{ name: localizationConstants.completed, value: '1' },
	{ name: localizationConstants.pending, value: '0' }
];

const exportTypeDropdownOptions = [
	{ name: localizationConstants.exportXLSXFiltered, value: 'xlsx' },
	{ name: localizationConstants.exportCSVFiltered, value: 'csv' },
	{ name: localizationConstants.exportCSVComplete, value: 'csv_full' },
];

const monthDropDownOptions = [
	{ id: 1, label: localizationConstants.january },
	{ id: 2, label: localizationConstants.february },
	{ id: 3, label: localizationConstants.march },
	{ id: 4, label: localizationConstants.april },
	{ id: 5, label: localizationConstants.may },
	{ id: 6, label: localizationConstants.june },
	{ id: 7, label: localizationConstants.july },
	{ id: 8, label: localizationConstants.august },
	{ id: 9, label: localizationConstants.september },
	{ id: 10, label: localizationConstants.october },
	{ id: 11, label: localizationConstants.november },
	{ id: 12, label: localizationConstants.december },
];

const versionDropdownOptions = [
	{ name: localizationConstants.demo, value: localizationConstants.demo },
	{ name: localizationConstants.fun, value: localizationConstants.fun },
	{ name: localizationConstants.standard, value: localizationConstants.standard },
	{ name: localizationConstants.sql, value: localizationConstants.sql }
];

const portalAccountStatusOptions = [
	{ name: localizationConstants.yes, value: '1' },
	{ name: localizationConstants.no, value: '0' },
];

const paymentInfo = {
	IBAN: 'EN86570501200000257238',
	BIC: 'MALADE51KOB'
};

const fiskalyPersonTypeDropdownOptions = [
	{ name: localizationConstants.natural, value: 'natural' },
	{ name: localizationConstants.legal, value: 'legal' },
];

const fiskalyGenderDropdownOptions = [
	{ name: localizationConstants.male, value: '1' },
	{ name: localizationConstants.female, value: '2' },
	{ name: localizationConstants.other, value: '3' },
];

const fiskalyLegalFormDropdownOptions = [
	{ name: localizationConstants.aktiengesellschaft, value: '31' },
	{ name: localizationConstants.aktiengesellschaftUCoKG, value: '25' },
	{ name: localizationConstants.aktiengesellschaftUCoOHG, value: '26' },
	{ name: localizationConstants.andereErwerbsOderWirtschaftsgenossenschaft, value: '49' },
	{ name: localizationConstants.atypischeStilleGesellschaft, value: '20' },
	{ name: localizationConstants.auslandischeKorpergesellschaft, value: '91' },
	{ name: localizationConstants.auslandischePersonengesellschaft, value: '92' },
	{ name: localizationConstants.bergrechtlicheGewerkschaft, value: '34' },
	{ name: localizationConstants.europaischeAktiengesellschaft, value: '36' },
	{ name: localizationConstants.europaischeWirtschaftlicheInteressenvereinigung, value: '28' },
	{ name: localizationConstants.gebietskorpergesellschaft, value: '81' },
	{ name: localizationConstants.gesellschaftDesBurgerlichenRechts, value: '27' },
	{ name: localizationConstants.gesellschaftMitBeschrankterHaftung, value: '35' },
	{ name: localizationConstants.gmbHUCoKG, value: '23' },
	{ name: localizationConstants.gmbHUCoOHG, value: '24' },
	{ name: localizationConstants.kolonialgesellschaft, value: '33' },
	{ name: localizationConstants.kommanditgesellschaft, value: '22' },
	{ name: localizationConstants.kommanditgesellschaftAufAktien, value: '32' },
	{ name: localizationConstants.creditCooperative, value: '41' },
	{ name: localizationConstants.landwirtschaftlicheNutzungsOderVerwaltungsgenossenschaft, value: '43' },
	{ name: localizationConstants.nichtrechtsfahigerVereinAnstaltStiftungOderAnderesZweckvermogen, value: '61' },
	{ name: localizationConstants.offeneHandelsgesellschaft, value: '21' },
	{ name: localizationConstants.offentlicheOderUnterStaatsaufsichtStehendeSparkasse, value: '72' },
	{ name: localizationConstants.offentlichRechtlicheReligionsgesellschaft, value: '82' },
	{ name: localizationConstants.offentlichRechtlicheVersorgungsVerkehrsOderHafenbetrieb, value: '74' },
	{ name: localizationConstants.personMitBeteiligungenAnGewerblPersonengesellschaften, value: '16' },
	{ name: localizationConstants.realgemeinde, value: '44' },
	{ name: localizationConstants.sceEuropaischeGenossenschaft, value: '45' },
	{ name: localizationConstants.sonstigeAuslandischeRechtsform, value: '99' },
	{ name: localizationConstants.sonstigeJuristischePersonDesPrivatenRechts, value: '59' },
	{ name: localizationConstants.sonstigeJuristischePersonenDesOffentlichenRechts, value: '83' },
	{ name: localizationConstants.sonstigeKapitalgesellschaft, value: '39' },
	{ name: localizationConstants.sonstigeKreditanstaltDesOffentlichenRechts, value: '73' },
	{ name: localizationConstants.sonstigePersonengesellschaft, value: '29' },
	{ name: localizationConstants.sonstigerBetriebGewerblicherArtVonJurPersonenDesOffentlichenRechts, value: '79' },
	{ name: localizationConstants.staatsbank, value: '71' },
	{ name: localizationConstants.unternehmergesellschaft, value: '37' },
	{ name: localizationConstants.versicherungsvereinAufGegenseitigkeit, value: '51' },
	{ name: localizationConstants.centralCashOffice, value: '42' },
];

const fiskalyCountryDropdownOptions = [
    { name: 'Germany', value: 'DE' },
	{ name: 'Afghanistan', value: 'AF' },
    { name: 'Ägypten', value: 'EG' },
    { name: 'Alandinseln', value: 'AX' },
    { name: 'Albanien', value: 'AL' },
    { name: 'Algerien', value: 'DZ' },
    { name: 'Amerikanische Jungferninseln', value: 'VI' },
    { name: 'Amerikanisch-Ozeanien (US Minor Outlying Islands)', value: 'UM' },
    { name: 'Amerikanisch-Samoa', value: 'AS' },
    { name: 'Andorra', value: 'AD' },
    { name: 'Angola', value: 'AO' },
    { name: 'Anguilla', value: 'AI' },
    { name: 'Antarktis', value: 'AQ' },
    { name: 'Antigua und Barbuda', value: 'AG' },
    { name: 'Äquatorialguinea', value: 'GQ' },
    { name: 'Argentinien', value: 'AR' },
    { name: 'Armenien', value: 'AM' },
    { name: 'Aruba', value: 'AW' },
    { name: 'Aserbaidschan', value: 'AZ' },
    { name: 'Äthiopien', value: 'ET' },
    { name: 'Australien', value: 'AU' },
    { name: 'Bahamas', value: 'BS' },
    { name: 'Bahrain', value: 'BH' },
    { name: 'Bangladesch', value: 'BD' },
    { name: 'Barbados', value: 'BB' },
    { name: 'Belarus (Weißrussland)', value: 'BY' },
    { name: 'Belgien', value: 'BE' },
    { name: 'Belize', value: 'BZ' },
    { name: 'Benin', value: 'BJ' },
    { name: 'Bermuda', value: 'BM' },
    { name: 'Bhutan', value: 'BT' },
    { name: 'Bolivien, Plurinationaler Staat', value: 'BO' },
    { name: 'Bonaire, Saba und Sint Eustatius', value: 'BQ' },
    { name: 'Bosnien und Herzegowina', value: 'BA' },
    { name: 'Botsuana', value: 'BW' },
    { name: 'Bouvetinsel', value: 'BV' },
    { name: 'Brasilien', value: 'BR' },
    { name: 'Britische Jungferninseln', value: 'VG' },
    { name: 'Britisches Territorium im Indischen Ozean', value: 'IO' },
    { name: 'Brunei Darussalam', value: 'BN' },
    { name: 'Bulgarien', value: 'BG' },
    { name: 'Burkina Faso', value: 'BF' },
    { name: 'Burundi', value: 'BI' },
    { name: 'Chile', value: 'CL' },
    { name: 'China, Volksrepublik', value: 'CN' },
    { name: 'Cookinseln', value: 'CK' },
    { name: 'Costa Rica', value: 'CR' },
    { name: 'Cote d Ivoire (Elfenbeinküste)', value: 'CI' },
    { name: 'Curacao', value: 'CW' },
    { name: 'Dänemark', value: 'DK' },
    { name: 'Dominica', value: 'DM' },
    { name: 'Dominikanische Republik', value: 'DO' },
    { name: 'Dschibuti', value: 'DJ' },
    { name: 'Ecuador', value: 'EC' },
    { name: 'El Salvador', value: 'SV' },
    { name: 'Eritrea', value: 'ER' },
    { name: 'Estland', value: 'EE' },
    { name: 'Falklandinseln (Malwinen)', value: 'FK' },
    { name: 'Färöer', value: 'FO' },
    { name: 'Fidschi', value: 'FJ' },
    { name: 'Finnland', value: 'FI' },
    { name: 'Frankreich', value: 'FR' },
    { name: 'Französische Südgebiete', value: 'TF' },
    { name: 'Französisch-Guayana', value: 'GF' },
    { name: 'Französisch-Polynesien', value: 'PF' },
    { name: 'Gabun', value: 'GA' },
    { name: 'Gambia', value: 'GM' },
    { name: 'Georgien', value: 'GE' },
    { name: 'Ghana', value: 'GH' },
    { name: 'Gibraltar', value: 'GI' },
    { name: 'Grenada', value: 'GD' },
    { name: 'Griechenland', value: 'GR' },
    { name: 'Grönland', value: 'GL' },
    { name: 'Guadeloupe', value: 'GP' },
    { name: 'Guam', value: 'GU' },
    { name: 'Guatemala', value: 'GT' },
    { name: 'Guernsey', value: 'GG' },
    { name: 'Guinea', value: 'GN' },
    { name: 'Guinea-Bissau', value: 'GW' },
    { name: 'Guyana', value: 'GY' },
    { name: 'Haiti', value: 'HT' },
    { name: 'Heard und die McDonaldinseln', value: 'HM' },
    { name: 'Heiliger Stuhl (Vatikanstadt)', value: 'VA' },
    { name: 'Honduras', value: 'HN' },
    { name: 'Hongkong', value: 'HK' },
    { name: 'Indien', value: 'IN' },
    { name: 'Indonesien', value: 'ID' },
    { name: 'Insel Man', value: 'IM' },
    { name: 'Irak', value: 'IQ' },
    { name: 'Iran, Islamische Republik', value: 'IR' },
    { name: 'Irland', value: 'IE' },
    { name: 'Island', value: 'IS' },
    { name: 'Israel', value: 'IL' },
    { name: 'Italien', value: 'IT' },
    { name: 'Jamaika', value: 'JM' },
    { name: 'Japan', value: 'JP' },
    { name: 'Jemen', value: 'YE' },
    { name: 'Jersey', value: 'JE' },
    { name: 'Jordanien', value: 'JO' },
    { name: 'Kaimaninseln', value: 'KY' },
    { name: 'Kambodscha', value: 'KH' },
	{ name: 'Kamerun', value: 'CM' },
    { name: 'Kanada', value: 'CA' },
    { name: 'Kap Verde', value: 'CV' },
    { name: 'Kasachstan', value: 'KZ' },
    { name: 'Katar', value: 'QA' },
    { name: 'Kenia', value: 'KE' },
    { name: 'Kirgisistan', value: 'KG' },
    { name: 'Kiribati', value: 'KI' },
    { name: 'Kokosinseln (Keelinginseln)', value: 'CC' },
    { name: 'Kolumbien', value: 'CO' },
    { name: 'Komoren', value: 'KM' },
    { name: 'Kongo, Demokratische Republik', value: 'CD' },
    { name: 'Kongo, Republik', value: 'CG' },
    { name: 'Königreich Eswatini', value: 'SZ' },
    { name: 'Korea, Demokratische Volksrepublik (Nordkorea)', value: 'KP' },
    { name: 'Korea, Republik (Südkorea)', value: 'KR' },
    { name: 'Kosovo', value: 'KO' },
    { name: 'Kroatien', value: 'HR' },
    { name: 'Kuba', value: 'CU' },
    { name: 'Kuwait', value: 'KW' },
    { name: 'Laos, Demokratische Volksrepublik', value: 'LA' },
    { name: 'Lesotho', value: 'LS' },
    { name: 'Lettland', value: 'LV' },
    { name: 'Libanon', value: 'LB' },
    { name: 'Liberia', value: 'LR' },
    { name: 'Libysch-Arabische Dschamahirija (Libyen)', value: 'LY' },
    { name: 'Liechtenstein', value: 'LI' },
    { name: 'Litauen', value: 'LT' },
    { name: 'Luxemburg', value: 'LU' },
    { name: 'Macau', value: 'MO' },
    { name: 'Madagaskar', value: 'MG' },
    { name: 'Malawi', value: 'MW' },
    { name: 'Malaysia', value: 'MY' },
    { name: 'Malediven', value: 'MV' },
    { name: 'Mali', value: 'ML' },
    { name: 'Malta', value: 'MT' },
    { name: 'Marianen, Nördliche', value: 'MP' },
    { name: 'Marokko', value: 'MA' },
    { name: 'Marshallinseln', value: 'MH' },
    { name: 'Martinique', value: 'MQ' },
    { name: 'Mauretanien', value: 'MR' },
    { name: 'Mauritius', value: 'MU' },
    { name: 'Mayotte', value: 'YT' },
    { name: 'Mexiko', value: 'MX' },
    { name: 'Mikronesien, Föderierte Staaten von', value: 'FM' },
    { name: 'Moldau, Republik', value: 'MD' },
    { name: 'Monaco', value: 'MC' },
    { name: 'Mongolei', value: 'MN' },
    { name: 'Montenegro', value: 'ME' },
    { name: 'Montserrat', value: 'MS' },
    { name: 'Mosambik', value: 'MZ' },
    { name: 'Myanmar', value: 'MM' },
    { name: 'Namibia', value: 'NA' },
    { name: 'Nauru', value: 'NR' },
    { name: 'Nepal', value: 'NP' },
    { name: 'Neukaledonien', value: 'NC' },
    { name: 'Neuseeland', value: 'NZ' },
    { name: 'Nicaragua', value: 'NI' },
    { name: 'Niederlande', value: 'NL' },
    { name: 'Niger', value: 'NE' },
    { name: 'Nigeria', value: 'NG' },
    { name: 'Niue', value: 'NU' },
    { name: 'Nordmazedonien', value: 'MK' },
    { name: 'Norfolkinsel', value: 'NF' },
    { name: 'Norwegen', value: 'NO' },
    { name: 'Oman', value: 'OM' },
    { name: 'Österreich', value: 'AT' },
    { name: 'Pakistan', value: 'PK' },
    { name: 'Palästinensische Autonomiegebiete', value: 'PS' },
    { name: 'Palau', value: 'PW' },
    { name: 'Panama', value: 'PA' },
    { name: 'Papua-Neuguinea', value: 'PG' },
    { name: 'Paraguay', value: 'PY' },
    { name: 'Peru', value: 'PE' },
    { name: 'Philippinen', value: 'PH' },
    { name: 'Pitcairninseln', value: 'PN' },
    { name: 'Polen', value: 'PL' },
    { name: 'Portugal', value: 'PT' },
    { name: 'Puerto Rico', value: 'PR' },
    { name: 'Reunion', value: 'RE' },
    { name: 'Ruanda', value: 'RW' },
    { name: 'Rumänien', value: 'RO' },
    { name: 'Russische Föderation', value: 'RU' },
    { name: 'Salomonen', value: 'SB' },
    { name: 'Sambia', value: 'ZM' },
    { name: 'Samoa', value: 'WS' },
    { name: 'San Marino', value: 'SM' },
    { name: 'Sao Tome und Principe', value: 'ST' },
    { name: 'Saudi-Arabien', value: 'SA' },
    { name: 'Schweden', value: 'SE' },
    { name: 'Schweiz', value: 'CH' },
    { name: 'Senegal', value: 'SN' },
    { name: 'Serbien', value: 'RS' },
    { name: 'Seychellen', value: 'SC' },
    { name: 'Sierra Leone', value: 'SL' },
    { name: 'Simbabwe', value: 'ZW' },
    { name: 'Singapur', value: 'SG' },
    { name: 'Sint Maarten (Niederländisches Gebiet)', value: 'SX' },
    { name: 'Slowakei', value: 'SK' },
    { name: 'Slowenien', value: 'SI' },
    { name: 'Somalia', value: 'SO' },
    { name: 'Spanien', value: 'ES' },
    { name: 'Sri Lanka', value: 'LK' },
	{ name: 'St. Barthelemy', value: 'BL' },
    { name: 'St. Helena und Nebengebiete', value: 'SH' },
    { name: 'St. Kitts und Nevis', value: 'KN' },
    { name: 'St. Lucia', value: 'LC' },
    { name: 'St. Martin (französischer Teil)', value: 'MF' },
    { name: 'St. Pierre und Miquelon', value: 'PM' },
    { name: 'St. Vincent und die Grenadinen', value: 'VC' },
    { name: 'Südafrika', value: 'ZA' },
    { name: 'Sudan', value: 'SD' },
    { name: 'Südgeorgien und die Südlichen Sandwichinseln', value: 'GS' },
    { name: 'Südsudan', value: 'SS' },
    { name: 'Suriname', value: 'SR' },
    { name: 'Svalbard und Jan Mayen', value: 'SJ' },
    { name: 'Syrien, Arabische Republik', value: 'SY' },
    { name: 'Tadschikistan', value: 'TJ' },
    { name: 'Taiwan', value: 'TW' },
    { name: 'Tansania, Vereinigte Republik', value: 'TZ' },
    { name: 'Thailand', value: 'TH' },
    { name: 'Timor-Leste', value: 'TL' },
    { name: 'Togo', value: 'TG' },
    { name: 'Tokelau', value: 'TK' },
    { name: 'Tonga', value: 'TO' },
    { name: 'Trinidad und Tobago', value: 'TT' },
    { name: 'Tschad', value: 'TD' },
    { name: 'Tschechische Republik', value: 'CZ' },
    { name: 'Tunesien', value: 'TN' },
    { name: 'Türkei', value: 'TR' },
    { name: 'Turkmenistan', value: 'TM' },
    { name: 'Turks- und Caicosinseln', value: 'TC' },
    { name: 'Tuvalu', value: 'TV' },
    { name: 'Uganda', value: 'UG' },
    { name: 'Ukraine', value: 'UA' },
    { name: 'Ungarn', value: 'HU' },
    { name: 'Uruguay', value: 'UY' },
    { name: 'Usbekistan', value: 'UZ' },
    { name: 'Vanuatu', value: 'VU' },
    { name: 'Venezuela, Bolivarische Republik', value: 'VE' },
    { name: 'Vereinigte Arabischen Emirate', value: 'AE' },
    { name: 'Vereinigtes Königreich Großbritannien und Nordirland', value: 'GB' },
    { name: 'Vereinigte Staaten von Amerika', value: 'US' },
    { name: 'Vietnam', value: 'VN' },
    { name: 'Wallis und Futuna', value: 'WF' },
    { name: 'Weihnachtsinsel', value: 'CX' },
    { name: 'Westsahara', value: 'EH' },
    { name: 'Zentralafrikanische Republik', value: 'CF' },
    { name: 'Zypern', value: 'CY' }
];

const isoCountyCodeDropdownOptions = [
	{ name: 'Aruba', value: 'ABW' },
	{ name: 'Afghanistan', value: 'AFG' },
	{ name: 'Angola', value: 'AGO' },
	{ name: 'Anguilla', value: 'AIA' },
	{ name: 'Åland Islands', value: 'ALA' },
	{ name: 'Albania', value: 'ALB' },
	{ name: 'Andorra', value: 'AND' },
	{ name: 'United Arab Emirates', value: 'ARE' },
	{ name: 'Argentina', value: 'ARG' },
	{ name: 'Armenia', value: 'ARM' },
	{ name: 'American Samoa', value: 'ASM' },
	{ name: 'Antarctica', value: 'ATA' },
	{ name: 'French Southern Territories', value: 'ATF' },
	{ name: 'Antigua and Barbuda', value: 'ATG' },
	{ name: 'Australia', value: 'AUS' },
	{ name: 'Austria', value: 'AUT' },
	{ name: 'Azerbaijan', value: 'AZE' },
	{ name: 'Burundi', value: 'BDI' },
	{ name: 'Belgium', value: 'BEL' },
	{ name: 'Benin', value: 'BEN' },
	{ name: 'Bonaire, Sint Eustatius and Saba', value: 'BES' },
	{ name: 'Burkina Faso', value: 'BFA' },
	{ name: 'Bangladesh', value: 'BGD' },
	{ name: 'Bulgaria', value: 'BGR' },
	{ name: 'Bahrain', value: 'BHR' },
	{ name: 'Bahamas', value: 'BHS' },
	{ name: 'Bosnia and Herzegovina', value: 'BIH' },
	{ name: 'Saint Barthélemy', value: 'BLM' },
	{ name: 'Belarus', value: 'BLR' },
	{ name: 'Belize', value: 'BLZ' },
	{ name: 'Bermuda', value: 'BMU' },
	{ name: 'Bolivia, Plurinational State of', value: 'BOL' },
	{ name: 'Brazil', value: 'BRA' },
	{ name: 'Barbados', value: 'BRB' },
	{ name: 'Brunei Darussalam', value: 'BRN' },
	{ name: 'Bhutan', value: 'BTN' },
	{ name: 'Bouvet Island', value: 'BVT' },
	{ name: 'Botswana', value: 'BWA' },
	{ name: 'Central African Republic', value: 'CAF' },
	{ name: 'Canada', value: 'CAN' },
	{ name: 'Cocos (Keeling) Islands', value: 'CCK' },
	{ name: 'Switzerland', value: 'CHE' },
	{ name: 'Chile', value: 'CHL' },
	{ name: 'China', value: 'CHN' },
	{ name: 'Côte d\'Ivoire', value: 'CIV' },
	{ name: 'Cameroon', value: 'CMR' },
	{ name: 'Congo, Democratic Republic of the', value: 'COD' },
	{ name: 'Congo', value: 'COG' },
	{ name: 'Cook Islands', value: 'COK' },
	{ name: 'Colombia', value: 'COL' },
	{ name: 'Comoros', value: 'COM' },
	{ name: 'Cabo Verde', value: 'CPV' },
	{ name: 'Costa Rica', value: 'CRI' },
	{ name: 'Cuba', value: 'CUB' },
	{ name: 'Curaçao', value: 'CUW' },
	{ name: 'Christmas Island', value: 'CXR' },
	{ name: 'Cayman Islands', value: 'CYM' },
	{ name: 'Cyprus', value: 'CYP' },
	{ name: 'Czechia', value: 'CZE' },
	{ name: 'Germany', value: 'DEU' },
	{ name: 'Djibouti', value: 'DJI' },
	{ name: 'Dominica', value: 'DMA' },
	{ name: 'Denmark', value: 'DNK' },
	{ name: 'Dominican Republic', value: 'DOM' },
	{ name: 'Algeria', value: 'DZA' },
	{ name: 'Ecuador', value: 'ECU' },
	{ name: 'Egypt', value: 'EGY' },
	{ name: 'Eritrea', value: 'ERI' },
	{ name: 'Western Sahara', value: 'ESH' },
	{ name: 'Spain', value: 'ESP' },
	{ name: 'Estonia', value: 'EST' },
	{ name: 'Ethiopia', value: 'ETH' },
	{ name: 'Finland', value: 'FIN' },
	{ name: 'Fiji', value: 'FJI' },
	{ name: 'Falkland Islands (Malvinas)', value: 'FLK' },
	{ name: 'France', value: 'FRA' },
	{ name: 'Faroe Islands', value: 'FRO' },
	{ name: 'Micronesia, Federated States of', value: 'FSM' },
	{ name: 'Gabon', value: 'GAB' },
	{ name: 'United Kingdom of Great Britain and Northern Ireland', value: 'GBR' },
	{ name: 'Georgia', value: 'GEO' },
	{ name: 'Guernsey', value: 'GGY' },
	{ name: 'Ghana', value: 'GHA' },
	{ name: 'Gibraltar', value: 'GIB' },
	{ name: 'Guinea', value: 'GIN' },
	{ name: 'Guadeloupe', value: 'GLP' },
	{ name: 'Gambia', value: 'GMB' },
	{ name: 'Guinea-Bissau', value: 'GNB' },
	{ name: 'Equatorial Guinea', value: 'GNQ' },
	{ name: 'Greece', value: 'GRC' },
	{ name: 'Grenada', value: 'GRD' },
	{ name: 'Greenland', value: 'GRL' },
	{ name: 'Guatemala', value: 'GTM' },
	{ name: 'French Guiana', value: 'GUF' },
	{ name: 'Guam', value: 'GUM' },
	{ name: 'Guyana', value: 'GUY' },
	{ name: 'Hong Kong', value: 'HKG' },
	{ name: 'Heard Island and McDonald Islands', value: 'HMD' },
	{ name: 'Honduras', value: 'HND' },
	{ name: 'Croatia', value: 'HRV' },
	{ name: 'Haiti', value: 'HTI' },
	{ name: 'Hungary', value: 'HUN' },
	{ name: 'Indonesia', value: 'IDN' },
	{ name: 'Isle of Man', value: 'IMN' },
	{ name: 'India', value: 'IND' },
	{ name: 'British Indian Ocean Territory', value: 'IOT' },
	{ name: 'Ireland', value: 'IRL' },
	{ name: 'Iran, Islamic Republic of', value: 'IRN' },
	{ name: 'Iraq', value: 'IRQ' },
	{ name: 'Iceland', value: 'ISL' },
	{ name: 'Israel', value: 'ISR' },
	{ name: 'Italy', value: 'ITA' },
	{ name: 'Jamaica', value: 'JAM' },
	{ name: 'Jersey', value: 'JEY' },
	{ name: 'Jordan', value: 'JOR' },
	{ name: 'Japan', value: 'JPN' },
	{ name: 'Kazakhstan', value: 'KAZ' },
	{ name: 'Kenya', value: 'KEN' },
	{ name: 'Kyrgyzstan', value: 'KGZ' },
	{ name: 'Cambodia', value: 'KHM' },
	{ name: 'Kiribati', value: 'KIR' },
	{ name: 'Saint Kitts and Nevis', value: 'KNA' },
	{ name: 'Korea, Republic of', value: 'KOR' },
	{ name: 'Kuwait', value: 'KWT' },
	{ name: 'Lao People\'s Democratic Republic', value: 'LAO' },
	{ name: 'Lebanon', value: 'LBN' },
	{ name: 'Liberia', value: 'LBR' },
	{ name: 'Libya', value: 'LBY' },
	{ name: 'Saint Lucia', value: 'LCA' },
	{ name: 'Liechtenstein', value: 'LIE' },
	{ name: 'Sri Lanka', value: 'LKA' },
	{ name: 'Lesotho', value: 'LSO' },
	{ name: 'Lithuania', value: 'LTU' },
	{ name: 'Luxembourg', value: 'LUX' },
	{ name: 'Latvia', value: 'LVA' },
	{ name: 'Macao', value: 'MAC' },
	{ name: 'Saint Martin (French part)', value: 'MAF' },
	{ name: 'Morocco', value: 'MAR' },
	{ name: 'Monaco', value: 'MCO' },
	{ name: 'Moldova, Republic of', value: 'MDA' },
	{ name: 'Madagascar', value: 'MDG' },
	{ name: 'Maldives', value: 'MDV' },
	{ name: 'Mexico', value: 'MEX' },
	{ name: 'Marshall Islands', value: 'MHL' },
	{ name: 'North Macedonia', value: 'MKD' },
	{ name: 'Mali', value: 'MLI' },
	{ name: 'Malta', value: 'MLT' },
	{ name: 'Myanmar', value: 'MMR' },
	{ name: 'Montenegro', value: 'MNE' },
	{ name: 'Mongolia', value: 'MNG' },
	{ name: 'Northern Mariana Islands', value: 'MNP' },
	{ name: 'Mozambique', value: 'MOZ' },
	{ name: 'Mauritania', value: 'MRT' },
	{ name: 'Montserrat', value: 'MSR' },
	{ name: 'Martinique', value: 'MTQ' },
	{ name: 'Mauritius', value: 'MUS' },
	{ name: 'Malawi', value: 'MWI' },
	{ name: 'Malaysia', value: 'MYS' },
	{ name: 'Mayotte', value: 'MYT' },
	{ name: 'Namibia', value: 'NAM' },
	{ name: 'New Caledonia', value: 'NCL' },
	{ name: 'Niger', value: 'NER' },
	{ name: 'Norfolk Island', value: 'NFK' },
	{ name: 'Nigeria', value: 'NGA' },
	{ name: 'Nicaragua', value: 'NIC' },
	{ name: 'Niue', value: 'NIU' },
	{ name: 'Netherlands, Kingdom of the', value: 'NLD' },
	{ name: 'Norway', value: 'NOR' },
	{ name: 'Nepal', value: 'NPL' },
	{ name: 'Nauru', value: 'NRU' },
	{ name: 'New Zealand', value: 'NZL' },
	{ name: 'Oman', value: 'OMN' },
	{ name: 'Pakistan', value: 'PAK' },
	{ name: 'Panama', value: 'PAN' },
	{ name: 'Pitcairn', value: 'PCN' },
	{ name: 'Peru', value: 'PER' },
	{ name: 'Philippines', value: 'PHL' },
	{ name: 'Palau', value: 'PLW' },
	{ name: 'Papua New Guinea', value: 'PNG' },
	{ name: 'Poland', value: 'POL' },
	{ name: 'Puerto Rico', value: 'PRI' },
	{ name: 'Korea, Democratic People\'s Republic of', value: 'PRK' },
	{ name: 'Portugal', value: 'PRT' },
	{ name: 'Paraguay', value: 'PRY' },
	{ name: 'Palestine, State of', value: 'PSE' },
	{ name: 'French Polynesia', value: 'PYF' },
	{ name: 'Qatar', value: 'QAT' },
	{ name: 'Réunion', value: 'REU' },
	{ name: 'Romania', value: 'ROU' },
	{ name: 'Russian Federation', value: 'RUS' },
	{ name: 'Rwanda', value: 'RWA' },
	{ name: 'Saudi Arabia', value: 'SAU' },
	{ name: 'Sudan', value: 'SDN' },
	{ name: 'Senegal', value: 'SEN' },
	{ name: 'Singapore', value: 'SGP' },
	{ name: 'South Georgia and the South Sandwich Islands', value: 'SGS' },
	{ name: 'Saint Helena, Ascension and Tristan da Cunha', value: 'SHN' },
	{ name: 'Svalbard and Jan Mayen', value: 'SJM' },
	{ name: 'Solomon Islands', value: 'SLB' },
	{ name: 'Sierra Leone', value: 'SLE' },
	{ name: 'El Salvador', value: 'SLV' },
	{ name: 'San Marino', value: 'SMR' },
	{ name: 'Somalia', value: 'SOM' },
	{ name: 'Saint Pierre and Miquelon', value: 'SPM' },
	{ name: 'Serbia', value: 'SRB' },
	{ name: 'South Sudan', value: 'SSD' },
	{ name: 'Sao Tome and Principe', value: 'STP' },
	{ name: 'Suriname', value: 'SUR' },
	{ name: 'Slovakia', value: 'SVK' },
	{ name: 'Slovenia', value: 'SVN' },
	{ name: 'Sweden', value: 'SWE' },
	{ name: 'Eswatini', value: 'SWZ' },
	{ name: 'Sint Maarten (Dutch part)', value: 'SXM' },
	{ name: 'Seychelles', value: 'SYC' },
	{ name: 'Syrian Arab Republic', value: 'SYR' },
	{ name: 'Turks and Caicos Islands', value: 'TCA' },
	{ name: 'Chad', value: 'TCD' },
	{ name: 'Togo', value: 'TGO' },
	{ name: 'Thailand', value: 'THA' },
	{ name: 'Tajikistan', value: 'TJK' },
	{ name: 'Tokelau', value: 'TKL' },
	{ name: 'Turkmenistan', value: 'TKM' },
	{ name: 'Timor-Leste', value: 'TLS' },
	{ name: 'Tonga', value: 'TON' },
	{ name: 'Trinidad and Tobago', value: 'TTO' },
	{ name: 'Tunisia', value: 'TUN' },
	{ name: 'Türkiye', value: 'TUR' },
	{ name: 'Tuvalu', value: 'TUV' },
	{ name: 'Taiwan, Province of China', value: 'TWN' },
	{ name: 'Tanzania, United Republic of', value: 'TZA' },
	{ name: 'Uganda', value: 'UGA' },
	{ name: 'Ukraine', value: 'UKR' },
	{ name: 'United States Minor Outlying Islands', value: 'UMI' },
	{ name: 'Uruguay', value: 'URY' },
	{ name: 'United States of America', value: 'USA' },
	{ name: 'Uzbekistan', value: 'UZB' },
	{ name: 'Holy See', value: 'VAT' },
	{ name: 'Saint Vincent and the Grenadines', value: 'VCT' },
	{ name: 'Venezuela, Bolivarian Republic of', value: 'VEN' },
	{ name: 'Virgin Islands (British)', value: 'VGB' },
	{ name: 'Virgin Islands (U.S.)', value: 'VIR' },
	{ name: 'Viet Nam', value: 'VNM' },
	{ name: 'Vanuatu', value: 'VUT' },
	{ name: 'Wallis and Futuna', value: 'WLF' },
	{ name: 'Samoa', value: 'WSM' },
	{ name: 'Yemen', value: 'YEM' },
	{ name: 'South Africa', value: 'ZAF' },
	{ name: 'Zambia', value: 'ZMB' },
	{ name: 'Zimbabwe', value: 'ZWE' }
];

const tssType = [
	{ name: localizationConstants.sdCard, value: '1' },
	{ name: localizationConstants.usbStick, value: '2' },
	{ name: localizationConstants.cloud, value: '3' }
];

const tssClientType = [
	{ name: localizationConstants.pcBasedPos, value: '1' },
	{ name: localizationConstants.tabletAppPos, value: '2' },
	{ name: localizationConstants.electronicCashRegister, value: '3' },
	{ name: localizationConstants.taximeter, value: '4' },
	{ name: localizationConstants.distanceMeter, value: '5' }
];

const pageOptions: number[] = [15, 30, 50, 100];

const PER_PAGE: number = 15;
const ENVIRONMENT = process.env.ENVIRONMENT === 'production' ? 'production' : 'staging';
const NUMBER_REGEX = /[0-9]*\.?[0-9]*$/;
const PASSWORD_VALIDATOR_REGEX = /^(?=.{8,})(?!.*[\s])(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+*!=]).*$/;
const EMAIL_VALIDATOR_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
const DATE_AND_TIME_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])*$/;

/**
 * reactSelectStyles - css style object for react-select
 */
const reactSelectStyles: StylesConfig<any, false, any> = {
	option: (base, state: any) => ({
		...base,
		'borderBottom': '1px solid #e7e7e7',
		'color': 'black',
		'padding': 8,
		'backgroundColor': state.isSelected ? '#d5d5d5' : state.isFocused ? '#e7e7e7' : '',
		':active': {
			backgroundColor: '#e7e7e7'
		},
		':hover': {
			backgroundColor: '#e7e7e7'
		},
		':focus': {
			backgroundColor: '#e7e7e7',
			outline: 0,
		}
	}),
	menu: base => ({
		...base,
		zIndex: 3,
		marginTop: 0
	}),
	menuList: base => ({
		...base,
		padding: 0,
		backgroundColor: '#ffffff !important',
	}),
	clearIndicator: base => ({
		...base,
		cursor: 'pointer'
	}),
	dropdownIndicator: (base, state: any) => ({
		...base,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
	}),
	indicatorSeparator: () => ({
		width: '0'
	}),
	control: () => ({
		// none of react-selects styles are passed to <View />
		display: 'flex',
		width: '100%',
		minHeight: '34px !important',
		border: '1px solid #e5e6e7',
		padding: '0px !important'
	}),
	singleValue: (base, state: any) => {
		const opacity = state.isDisabled ? 0.5 : 1;
		const transition = 'opacity 300ms';

		return { ...base, opacity: opacity, transition: transition };
	}
};

const revisionHistoryActions = {
	HardwareAdded: 'HardwareAdded',
	AssignedCustomer: 'AssignedCustomer',
	MachineUpdated: 'MachineUpdated',
	PaymentStatusUpdated: 'PaymentStatusUpdated',
	LicenseGenerated: 'LicenseGenerated',
	LicenseAdded: 'LicenseAdded',
	LicenseActivated: 'LicenseActivated',
	LicenseInactivated: 'LicenseInactivated',
	LicenseUpdated: 'LicenseUpdated',
	LicenseDeleted: 'LicenseDeleted',
	LicenseEmailSent: 'LicenseEmailSent',
	FutureLicenseActivated: 'FutureLicenseActivated',
	ModuleUpdated: 'ModuleUpdated',
	HardwareCloned: 'HardwareCloned',
	LicenseCloned: 'LicenseCloned',
};

let phoenixBranchMapper: any = {};

const phoenixBranchesOptions = async () => {
	try {
		const response = await HttpService.get(API_CONFIG.path.phoenixBraches);
		const phoenixBranches = response.map(({branch_name, branch_id}: any) => ({
			name: branch_name,
			value: branch_id
		}));
		response.forEach(({branch_name: branch_name_1, branch_id: branch_id_1}: any) => {
			phoenixBranchMapper[branch_id_1] = branch_name_1;
		});
		return ([{name: i18n.t(localizationConstants.all), value: ''}, ...phoenixBranches]);
	} catch {
		return [{name: i18n.t(localizationConstants.all), value: ''}];
	}
};

const tse_sample_file_url = 'https://universe-sample-data-examples.s3.eu-central-1.amazonaws.com/sample_tse_device.csv';

export {
	API_CONFIG, DATE_AND_TIME_REGEX, EMAIL_VALIDATOR_REGEX, ENVIRONMENT,
	NUMBER_REGEX,
	PASSWORD_VALIDATOR_REGEX, PER_PAGE, allPermissionMapping, autoUpdateOptions, birdHubOrderStatusMapper, branchIdDropdownOptions, branchIds, branchMapper, branchOptions, commissionStatusDropdownOptions, exportTypeDropdownOptions, credentialStatus,
	credentialType, dateFields, dateStatusDropdownOptions, firstLevelBreadcrumbs, getUrl, inventoryGroupFilterOptions, inventoryGroupStatusDropdownOptions, invoiceGroupStatusDropdownOptions, invoiceStatusActionOptions, isLockedDropdownOptions, isLockedDropdownOptionsForResigo, languageDropdownOptions, modeTypeDropdownOptions, modeTypeLabelMapper, mposDropdownOptions, orderStatusFilterDropDownOptions, pageOptions, paymentInfo, phoenixBranchMapper, phoenixBranchesOptions, portalAccountStatusOptions, productSelectionDropDownOption, provisionPercentageDropdownOptions, monthDropDownOptions as quarterDropDownOptions, reactSelectStyles, reportStatusDropdownOptions, resigoVersionOptions, revisionHistoryActions, searchDropdownOptions, sepaStatusOptionsForActive,
	bundleVisibilityStatusDropdownOptions,
	sepaStatusOptionsForBlock, sepaStatusOptionsForPending, statusDropdownOptions, tseDeviceTypeDropDownOptions, tse_sample_file_url,
	userStatus, userStatusDropdownOptions, versionDropdownOptions, passwordTime,
	colorDropdownOptions,
	fiskalyPersonTypeDropdownOptions, fiskalyGenderDropdownOptions, fiskalyLegalFormDropdownOptions, fiskalyCountryDropdownOptions, isoCountyCodeDropdownOptions, tssType, tssClientType
};
